import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sparkles, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { GamificationGoals, GamificationMetricKey, GamificationPeriodKey } from "@shared/schema";

type WorkspaceSettings = {
  id: string;
  workspaceId: string;
  enabled: boolean;
  defaultTheme: "equalizer" | "test_tubes" | "marquee";
  soundEnabled: boolean;
  callQualityGuard: boolean;
  emailQualityGuard: boolean;
  bookingConfirmedOnly: boolean;
  defaultGoals: GamificationGoals;
  stretchFactor: string;
  tenureRampEnabled: boolean;
  summaryCutoffHour: number;
};

type RepConfig = {
  id?: string;
  userId: string;
  widgetEnabled: boolean;
  theme: string | null;
  goalOverrides: GamificationGoals | null;
  tenureStartDate: string | null;
  endOfDaySummaryEnabled: boolean;
};

type RepRow = {
  userId: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  role?: string;
};

const METRIC_KEYS: GamificationMetricKey[] = ["activity", "pipeline", "wins", "revenueBooked"];

const GOALS_FALLBACK: GamificationGoals = {
  activity: { daily: 20, weekly: 100, monthly: 400 },
  pipeline: { daily: 3, weekly: 15, monthly: 60 },
  wins: { daily: 1, weekly: 5, monthly: 20 },
  revenueBooked: { daily: 500, weekly: 2500, monthly: 10000 },
};

function normalizeGoals(raw: Partial<GamificationGoals> | GamificationGoals | null | undefined): GamificationGoals {
  const out: GamificationGoals = JSON.parse(JSON.stringify(GOALS_FALLBACK));
  if (!raw) return out;
  (METRIC_KEYS as GamificationMetricKey[]).forEach((mk) => {
    const src = (raw as Partial<GamificationGoals>)[mk];
    if (!src) return;
    (["daily", "weekly", "monthly"] as GamificationPeriodKey[]).forEach((pk) => {
      const v = src[pk];
      if (typeof v === "number" && isFinite(v) && v >= 0) out[mk][pk] = v;
    });
  });
  return out;
}
const METRIC_LABELS: Record<GamificationMetricKey, string> = {
  activity: "Outreach",
  pipeline: "New Leads",
  wins: "Bookings",
  revenueBooked: "Revenue Booked",
};
const METRIC_IS_DOLLAR: Partial<Record<GamificationMetricKey, true>> = {
  revenueBooked: true,
};
const PERIOD_KEYS: GamificationPeriodKey[] = ["daily", "weekly", "monthly"];

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inPanel?: boolean;
}

export function GamificationSettingsDialog({ open, onOpenChange, inPanel }: DialogProps) {
  const content = <GamificationSettingsPanel />;
  if (inPanel)
    return (
      <div className="w-full h-full flex flex-col overflow-auto p-4 sm:p-6">
        {content}
      </div>
    );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Gamification Settings</DialogTitle>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
}

function GamificationSettingsPanel() {
  const { toast } = useToast();
  const { data: settings, isLoading } = useQuery<WorkspaceSettings>({
    queryKey: ["/api/gamification/workspace-settings"],
  });

  const [draft, setDraft] = useState<WorkspaceSettings | null>(null);
  useEffect(() => {
    if (settings) setDraft({ ...settings, defaultGoals: normalizeGoals(settings.defaultGoals) });
  }, [settings]);

  const save = useMutation({
    mutationFn: async (updates: Partial<WorkspaceSettings>) =>
      apiRequest("PATCH", "/api/gamification/workspace-settings", updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/gamification/workspace-settings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/gamification/snapshot"] });
      toast({ title: "Saved" });
    },
    onError: (e: any) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  if (isLoading || !draft) {
    return (
      <div className="p-6 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const updateGoal = (mk: GamificationMetricKey, pk: GamificationPeriodKey, v: number) => {
    setDraft((d) => {
      if (!d) return d;
      const goals = JSON.parse(JSON.stringify(d.defaultGoals)) as GamificationGoals;
      goals[mk][pk] = Math.max(0, Math.floor(v || 0));
      return { ...d, defaultGoals: goals };
    });
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <Tabs defaultValue="workspace">
        <TabsList>
          <TabsTrigger value="workspace" data-testid="tab-workspace">Workspace defaults</TabsTrigger>
          <TabsTrigger value="reps" data-testid="tab-reps">Per-rep configuration</TabsTrigger>
          <TabsTrigger value="recap-history" data-testid="tab-recap-history">Recap email history</TabsTrigger>
        </TabsList>

        <TabsContent value="workspace" className="space-y-6 mt-4">
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="enabled" className="text-sm font-semibold">Enable gamification widget</Label>
                  <p className="text-xs text-muted-foreground">Reps must opt in individually too.</p>
                </div>
                <Switch
                  id="enabled"
                  checked={draft.enabled}
                  onCheckedChange={(v) => setDraft({ ...draft, enabled: v })}
                  data-testid="switch-enabled"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="sound-en" className="text-sm font-semibold">Sound effects allowed</Label>
                  <p className="text-xs text-muted-foreground">Reps can still mute individually.</p>
                </div>
                <Switch
                  id="sound-en"
                  checked={draft.soundEnabled}
                  onCheckedChange={(v) => setDraft({ ...draft, soundEnabled: v })}
                  data-testid="switch-sound"
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="theme" className="text-sm font-semibold">Default theme</Label>
                <Select value={draft.defaultTheme} onValueChange={(v) => setDraft({ ...draft, defaultTheme: v as any })}>
                  <SelectTrigger className="w-44" id="theme" data-testid="select-default-theme">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="equalizer">Equalizer</SelectItem>
                    <SelectItem value="test_tubes">Test Tubes</SelectItem>
                    <SelectItem value="marquee">Marquee</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 space-y-3">
              <h4 className="font-semibold text-sm">Quality guards</h4>
              <p className="text-xs text-muted-foreground">Prevent shallow taps from inflating credit.</p>
              <div className="flex items-center justify-between">
                <Label htmlFor="qg-call" className="text-sm">Calls require notes</Label>
                <Switch id="qg-call" checked={draft.callQualityGuard}
                  onCheckedChange={(v) => setDraft({ ...draft, callQualityGuard: v })}
                  data-testid="switch-call-guard"
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="qg-email" className="text-sm">Emails need ≥50 words or same-day call</Label>
                <Switch id="qg-email" checked={draft.emailQualityGuard}
                  onCheckedChange={(v) => setDraft({ ...draft, emailQualityGuard: v })}
                  data-testid="switch-email-guard"
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="qg-book" className="text-sm">Bookings only count when confirmed</Label>
                <Switch id="qg-book" checked={draft.bookingConfirmedOnly}
                  onCheckedChange={(v) => setDraft({ ...draft, bookingConfirmedOnly: v })}
                  data-testid="switch-booking-guard"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 space-y-3">
              <h4 className="font-semibold text-sm">Default goals</h4>
              <p className="text-xs text-muted-foreground">Starting targets for new reps. Reps can have per-user overrides.</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-muted-foreground text-xs">
                    <tr>
                      <th className="text-left font-medium pb-2">Metric</th>
                      {PERIOD_KEYS.map((p) => (
                        <th key={p} className="text-right font-medium pb-2 capitalize">{p}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {METRIC_KEYS.map((mk) => (
                      <tr key={mk} className="border-t">
                        <td className="py-2 font-medium">{METRIC_LABELS[mk]}</td>
                        {PERIOD_KEYS.map((pk) => (
                          <td key={pk} className="py-2 pl-2">
                            <div className="relative ml-auto w-28">
                              {METRIC_IS_DOLLAR[mk] && (
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs pointer-events-none">$</span>
                              )}
                              <Input
                                type="number"
                                min={0}
                                value={draft.defaultGoals[mk][pk]}
                                onChange={(e) => updateGoal(mk, pk, Number(e.target.value))}
                                className={`h-8 w-full text-right ${METRIC_IS_DOLLAR[mk] ? "pl-5" : ""}`}
                                data-testid={`input-goal-${mk}-${pk}`}
                              />
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 space-y-3">
              <h4 className="font-semibold text-sm">Auto-suggest & ramp</h4>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label htmlFor="stretch" className="text-sm">Stretch factor</Label>
                  <p className="text-xs text-muted-foreground">Used by Auto-Suggest. 1.15 = +15% over recent average.</p>
                </div>
                <Input
                  id="stretch"
                  type="number"
                  step="0.05"
                  min="1"
                  max="2"
                  value={draft.stretchFactor}
                  onChange={(e) => setDraft({ ...draft, stretchFactor: e.target.value })}
                  className="w-24 text-right"
                  data-testid="input-stretch"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="ramp" className="text-sm">Tenure ramp</Label>
                  <p className="text-xs text-muted-foreground">Goals scale to 50% &lt; 30d, 75% &lt; 60d.</p>
                </div>
                <Switch id="ramp" checked={draft.tenureRampEnabled}
                  onCheckedChange={(v) => setDraft({ ...draft, tenureRampEnabled: v })}
                  data-testid="switch-ramp"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="cutoff" className="text-sm">End-of-day summary cutoff (hour, 0-23)</Label>
                </div>
                <Input
                  id="cutoff"
                  type="number"
                  min="0"
                  max="23"
                  value={draft.summaryCutoffHour}
                  onChange={(e) => setDraft({ ...draft, summaryCutoffHour: Number(e.target.value) })}
                  className="w-20 text-right"
                  data-testid="input-cutoff"
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button
              onClick={() => save.mutate(draft)}
              disabled={save.isPending}
              data-testid="button-save-workspace"
            >
              {save.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save settings
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="reps" className="mt-4">
          <PerRepConfigList />
        </TabsContent>

        <TabsContent value="recap-history" className="mt-4">
          <AdminRecapEmailHistory />
        </TabsContent>
      </Tabs>
    </div>
  );
}

type AdminRecapEntry = {
  id: string;
  workspaceId: string;
  userId: string;
  recapDate: string;
  sentAt: string;
  status: "success" | "skipped" | "error";
  errorMessage: string | null;
  userName: string;
  userEmail: string | null;
};

function AdminRecapEmailHistory() {
  const { toast } = useToast();
  const [userFilter, setUserFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<"all" | "success" | "skipped" | "error">("all");

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (userFilter) params.set("userId", userFilter);
    if (statusFilter !== "all") params.set("status", statusFilter);
    const s = params.toString();
    return s ? `?${s}` : "";
  }, [userFilter, statusFilter]);

  const { data, isLoading } = useQuery<{ entries: AdminRecapEntry[] }>({
    queryKey: ["/api/gamification/recap-email/history/admin", userFilter, statusFilter],
    queryFn: async () => {
      const r = await fetch(`/api/gamification/recap-email/history/admin${queryString}`, {
        credentials: "include",
      });
      if (!r.ok) throw new Error("Failed to load recap email history");
      return r.json();
    },
  });

  // Resend mutation for failed recap deliveries. The backend re-runs the
  // recap-build pipeline for the same rep + recap day and writes a *new*
  // log row tagged with `trigger="resend"` and `resendOfLogId` pointing at
  // the original failure, so the audit trail stays intact and the operator
  // can see "this row is what we tried after the original error". On
  // success we invalidate the admin history query so the new row appears.
  const resendMutation = useMutation({
    mutationFn: async (logId: string) => {
      return apiRequest("POST", `/api/gamification/recap-email/resend/${logId}`);
    },
    onSuccess: () => {
      toast({
        title: "Resend queued",
        description: "Recap email has been re-sent — check the history below for the new entry.",
      });
      void queryClient.invalidateQueries({
        queryKey: ["/api/gamification/recap-email/history/admin"],
      });
    },
    onError: (err: any) => {
      toast({
        title: "Resend failed",
        description: err?.message ?? "Could not resend recap email.",
        variant: "destructive",
      });
    },
  });

  const { data: profiles } = useQuery<RepRow[]>({
    queryKey: ["/api/users-with-profiles"],
    queryFn: async () => {
      const r = await fetch("/api/users-with-profiles", { credentials: "include" });
      if (!r.ok) return [];
      const arr = await r.json();
      return (Array.isArray(arr) ? arr : []).map((u: any) => ({
        userId: u.id,
        firstName: u.firstName,
        lastName: u.lastName,
        email: u.email,
        role: u.profile?.role,
      }));
    },
  });
  const reps = useMemo(
    () => (profiles ?? []).filter((p) => p.role === "sales_rep"),
    [profiles],
  );

  const formatTimestamp = (iso: string) => {
    try {
      return new Date(iso).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const statusBadge = (status: "success" | "skipped" | "error") => {
    const cls =
      status === "success"
        ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
        : status === "error"
          ? "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200"
          : "bg-muted text-muted-foreground";
    return (
      <span
        className={`inline-block text-xs uppercase tracking-wide px-2 py-0.5 rounded ${cls}`}
        data-testid={`badge-status-${status}`}
      >
        {status}
      </span>
    );
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <div>
          <Label className="text-sm font-semibold">Recap email delivery history</Label>
          <p className="text-xs text-muted-foreground">
            Last 14 days of recap email send attempts across the workspace. Filter by rep or status to investigate Gmail-connector issues without digging through server logs.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1">
            <Label htmlFor="recap-history-rep" className="text-xs text-muted-foreground">Rep</Label>
            <Select
              value={userFilter || "all"}
              onValueChange={(v) => setUserFilter(v === "all" ? "" : v)}
            >
              <SelectTrigger id="recap-history-rep" data-testid="select-recap-history-rep">
                <SelectValue placeholder="All reps" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All reps</SelectItem>
                {reps.map((rep) => {
                  const name = [rep.firstName, rep.lastName].filter(Boolean).join(" ")
                    || rep.email
                    || rep.userId;
                  return (
                    <SelectItem key={rep.userId} value={rep.userId}>
                      {name}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <Label htmlFor="recap-history-status" className="text-xs text-muted-foreground">Status</Label>
            <Select
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}
            >
              <SelectTrigger id="recap-history-status" data-testid="select-recap-history-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="error">Error</SelectItem>
                <SelectItem value="skipped">Skipped</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center text-xs text-muted-foreground py-3">
            <Loader2 className="w-3 h-3 animate-spin mr-2" /> Loading delivery history…
          </div>
        ) : !data?.entries?.length ? (
          <p
            className="text-sm text-muted-foreground py-3"
            data-testid="text-admin-recap-history-empty"
          >
            No recap email send attempts match the current filters.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="table-admin-recap-history">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b">
                  <th className="py-2 pr-4">Rep</th>
                  <th className="py-2 pr-4">Recap day</th>
                  <th className="py-2 pr-4">Sent</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Detail</th>
                  <th className="py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.entries.map((e) => {
                  const isResending =
                    resendMutation.isPending && resendMutation.variables === e.id;
                  return (
                    <tr
                      key={e.id}
                      className="border-b last:border-b-0"
                      data-testid={`row-admin-recap-${e.id}`}
                    >
                      <td className="py-2 pr-4">
                        <div className="font-medium" data-testid={`text-admin-recap-rep-${e.id}`}>
                          {e.userName}
                        </div>
                        {e.userEmail && (
                          <div className="text-xs text-muted-foreground">{e.userEmail}</div>
                        )}
                      </td>
                      <td className="py-2 pr-4" data-testid={`text-admin-recap-date-${e.id}`}>
                        {e.recapDate}
                      </td>
                      <td className="py-2 pr-4 text-xs text-muted-foreground">
                        {formatTimestamp(e.sentAt)}
                      </td>
                      <td className="py-2 pr-4">{statusBadge(e.status)}</td>
                      <td
                        className="py-2 pr-4 text-xs text-muted-foreground max-w-[24ch] truncate"
                        title={e.errorMessage ?? ""}
                        data-testid={`text-admin-recap-detail-${e.id}`}
                      >
                        {e.errorMessage ?? "—"}
                      </td>
                      <td className="py-2 text-right">
                        {e.status === "error" ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={isResending}
                            onClick={() => resendMutation.mutate(e.id)}
                            data-testid={`button-resend-recap-${e.id}`}
                          >
                            {isResending ? (
                              <>
                                <Loader2 className="w-3 h-3 animate-spin mr-1" /> Resending…
                              </>
                            ) : (
                              "Resend"
                            )}
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PerRepConfigList() {
  const { toast } = useToast();
  const { data: profiles } = useQuery<RepRow[]>({
    queryKey: ["/api/users-with-profiles"],
    queryFn: async () => {
      const r = await fetch("/api/users-with-profiles", { credentials: "include" });
      if (!r.ok) return [];
      const data = await r.json();
      return (Array.isArray(data) ? data : []).map((u: any) => ({
        userId: u.id,
        firstName: u.firstName,
        lastName: u.lastName,
        email: u.email,
        role: u.profile?.role,
      }));
    },
  });
  const reps = useMemo(() => (profiles ?? []).filter((p) => p.role === "sales_rep"), [profiles]);
  const [selected, setSelected] = useState<string | null>(null);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Card className="md:col-span-1">
        <CardContent className="p-2 max-h-[480px] overflow-y-auto">
          {reps.length === 0 && (
            <p className="text-xs text-muted-foreground p-3">No sales reps in this workspace yet.</p>
          )}
          {reps.map((rep) => {
            const name = [rep.firstName, rep.lastName].filter(Boolean).join(" ") || rep.email || rep.userId;
            return (
              <button
                key={rep.userId}
                type="button"
                onClick={() => setSelected(rep.userId)}
                className={`w-full text-left p-2 rounded text-sm hover-elevate ${selected === rep.userId ? "bg-muted" : ""}`}
                data-testid={`button-select-rep-${rep.userId}`}
              >
                <div className="font-medium truncate">{name}</div>
                <div className="text-xs text-muted-foreground truncate">{rep.email}</div>
              </button>
            );
          })}
        </CardContent>
      </Card>
      <div className="md:col-span-2">
        {selected ? <RepConfigEditor userId={selected} /> : (
          <p className="text-sm text-muted-foreground p-4">Select a rep to override their goals or theme.</p>
        )}
      </div>
    </div>
  );
}

function RepConfigEditor({ userId }: { userId: string }) {
  const { toast } = useToast();
  const { data: workspaceSettings } = useQuery<WorkspaceSettings>({
    queryKey: ["/api/gamification/workspace-settings"],
  });
  const { data: cfg } = useQuery<RepConfig>({
    queryKey: ["/api/gamification/rep-config", userId],
    queryFn: async () => {
      const r = await fetch(`/api/gamification/rep-config/${userId}`, { credentials: "include" });
      if (!r.ok) throw new Error("Failed to load");
      return r.json();
    },
  });

  const [goals, setGoals] = useState<GamificationGoals | null>(null);
  const [tenureStart, setTenureStart] = useState<string>("");
  const [theme, setTheme] = useState<string>("");
  const [widgetEnabled, setWidgetEnabled] = useState(true);

  useEffect(() => {
    if (cfg && workspaceSettings) {
      setGoals(normalizeGoals(cfg.goalOverrides ?? workspaceSettings.defaultGoals));
      setTenureStart(cfg.tenureStartDate ? String(cfg.tenureStartDate).slice(0, 10) : "");
      setTheme(cfg.theme ?? "");
      setWidgetEnabled(cfg.widgetEnabled);
    }
  }, [cfg, workspaceSettings]);

  const save = useMutation({
    mutationFn: async (updates: Partial<RepConfig>) =>
      apiRequest("PATCH", `/api/gamification/rep-config/${userId}`, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/gamification/rep-config", userId] });
      queryClient.invalidateQueries({ queryKey: ["/api/gamification/manager-roll-up"] });
      toast({ title: "Saved" });
    },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  const suggest = useMutation({
    mutationFn: async (): Promise<GamificationGoals> => {
      const r = await apiRequest("POST", `/api/gamification/auto-suggest/${userId}`, {});
      return r.json();
    },
    onSuccess: (g) => {
      setGoals(g);
      toast({ title: "Suggested goals applied (not yet saved)" });
    },
  });

  if (!goals) {
    return <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />;
  }

  const updateGoal = (mk: GamificationMetricKey, pk: GamificationPeriodKey, v: number) => {
    setGoals((g) => {
      if (!g) return g;
      const next = JSON.parse(JSON.stringify(g)) as GamificationGoals;
      next[mk][pk] = Math.max(0, Math.floor(v || 0));
      return next;
    });
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold">Widget enabled for this rep</Label>
          <Switch
            checked={widgetEnabled}
            onCheckedChange={setWidgetEnabled}
            data-testid="switch-rep-enabled"
          />
        </div>
        <div className="flex items-center justify-between">
          <Label className="text-sm">Theme override</Label>
          <Select value={theme || "__inherit"} onValueChange={(v) => setTheme(v === "__inherit" ? "" : v)}>
            <SelectTrigger className="w-44" data-testid="select-rep-theme">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__inherit">Inherit workspace</SelectItem>
              <SelectItem value="equalizer">Equalizer</SelectItem>
              <SelectItem value="test_tubes">Test Tubes</SelectItem>
              <SelectItem value="marquee">Marquee</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-between">
          <Label className="text-sm">Tenure start date</Label>
          <Input
            type="date"
            value={tenureStart}
            onChange={(e) => setTenureStart(e.target.value)}
            className="w-44"
            data-testid="input-tenure-start"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <h5 className="text-sm font-semibold">Goal overrides</h5>
            <Button
              variant="outline"
              size="sm"
              onClick={() => suggest.mutate()}
              disabled={suggest.isPending}
              data-testid="button-auto-suggest"
            >
              <Sparkles className="h-3.5 w-3.5 mr-1" />
              Auto-suggest
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-muted-foreground text-xs">
                <tr>
                  <th className="text-left font-medium pb-2">Metric</th>
                  {PERIOD_KEYS.map((p) => (
                    <th key={p} className="text-right font-medium pb-2 capitalize">{p}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {METRIC_KEYS.map((mk) => (
                  <tr key={mk} className="border-t">
                    <td className="py-2 font-medium">{METRIC_LABELS[mk]}</td>
                    {PERIOD_KEYS.map((pk) => (
                      <td key={pk} className="py-2 pl-2">
                        <div className="relative ml-auto w-28">
                          {METRIC_IS_DOLLAR[mk] && (
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs pointer-events-none">$</span>
                          )}
                          <Input
                            type="number"
                            min={0}
                            value={goals[mk][pk]}
                            onChange={(e) => updateGoal(mk, pk, Number(e.target.value))}
                            className={`h-8 w-full text-right ${METRIC_IS_DOLLAR[mk] ? "pl-5" : ""}`}
                            data-testid={`input-rep-goal-${mk}-${pk}`}
                          />
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => save.mutate({ goalOverrides: null as any })}
            data-testid="button-clear-overrides"
          >
            Clear overrides
          </Button>
          <Button
            onClick={() => save.mutate({
              widgetEnabled,
              theme: theme || null,
              tenureStartDate: tenureStart || null,
              goalOverrides: goals,
            } as any)}
            disabled={save.isPending}
            data-testid="button-save-rep"
          >
            {save.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save rep config
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
